/**
 * Security API wrapper — thin per-endpoint functions for /api/admin/security/*.
 *
 * Uses the authFetch from AuthContext (passed in by the caller) so auth-header
 * handling stays centralized.
 */

import type {
  AcknowledgeEventBody,
  CanonicalEventDetail,
  CanonicalEventFilters,
  CanonicalEventListResponse,
  CreateIntelSourceBody,
  DeleteIntelSourceBody,
  DeleteIntelSourceResponse,
  DismissFPBody,
  ForwardWiringEnqueueResponse,
  ForwardWiringQueueStatusResponse,
  IntelSource,
  IntelSourceUpdateBody,
  ListParsersResponse,
  ReactivateSourceResponse,
  ReopenEventBody,
  SendGroupedBody,
  SendGroupedResponse,
  SendToIssuesBody,
  SendToOrchestratorBody,
  SnoozeEventBody,
  StateTransitionResponse,
} from '../types/security';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

const BASE = '/api/admin/security';

function buildQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      // ignore
    }
    const err = new Error(
      `HTTP ${res.status}${detail && typeof detail === 'object' && 'detail' in detail
        ? `: ${(detail as { detail: string }).detail}`
        : ''}`,
    );
    (err as Error & { status?: number; detail?: unknown }).status = res.status;
    (err as Error & { status?: number; detail?: unknown }).detail = detail;
    throw err;
  }
  return res.json() as Promise<T>;
}

export const securityApi = {
  listCanonicalEvents: async (
    authFetch: AuthFetch,
    filters: CanonicalEventFilters = {},
  ): Promise<CanonicalEventListResponse> => {
    const qs = buildQuery({
      cve_id: filters.cve_id,
      min_max_severity: filters.min_max_severity,
      source_count_gte: filters.source_count_gte,
      latest_status: filters.latest_status,
      last_seen_after: filters.last_seen_after,
      affected_target: filters.affected_target,
      operator_state: filters.operator_state, // WQ-286 Item 4
      limit: filters.limit ?? 50,
      offset: filters.offset ?? 0,
    });
    const res = await authFetch(`${BASE}/events${qs}`);
    return jsonOrThrow<CanonicalEventListResponse>(res);
  },

  getCanonicalEvent: async (
    authFetch: AuthFetch,
    canonicalEventId: string,
  ): Promise<CanonicalEventDetail> => {
    const res = await authFetch(`${BASE}/events/${canonicalEventId}`);
    return jsonOrThrow<CanonicalEventDetail>(res);
  },

  listIntelSources: async (authFetch: AuthFetch): Promise<IntelSource[]> => {
    const res = await authFetch(`${BASE}/sources`);
    return jsonOrThrow<IntelSource[]>(res);
  },

  updateIntelSource: async (
    authFetch: AuthFetch,
    sourceId: string,
    body: IntelSourceUpdateBody,
  ): Promise<IntelSource> => {
    const res = await authFetch(`${BASE}/sources/${sourceId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return jsonOrThrow<IntelSource>(res);
  },

  sendToOrchestrator: async (
    authFetch: AuthFetch,
    canonicalEventId: string,
    body: SendToOrchestratorBody,
  ): Promise<ForwardWiringEnqueueResponse> => {
    const res = await authFetch(
      `${BASE}/events/${canonicalEventId}/send-to-orchestrator`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    return jsonOrThrow<ForwardWiringEnqueueResponse>(res);
  },

  // WQ-297/298 D4: package-grouped bulk send. Posts the selected canonical
  // event ids; the server resolves + groups by ecosystem+package and enqueues
  // one grouped row per package (cpe/None → legacy single fallback).
  sendGrouped: async (
    authFetch: AuthFetch,
    body: SendGroupedBody,
  ): Promise<SendGroupedResponse> => {
    const res = await authFetch(`${BASE}/orchestrator/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return jsonOrThrow<SendGroupedResponse>(res);
  },

  sendToIssues: async (
    authFetch: AuthFetch,
    canonicalEventId: string,
    body: SendToIssuesBody,
  ): Promise<ForwardWiringEnqueueResponse> => {
    const res = await authFetch(
      `${BASE}/events/${canonicalEventId}/send-to-issues`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    return jsonOrThrow<ForwardWiringEnqueueResponse>(res);
  },

  getForwardWiringQueueStatus: async (
    authFetch: AuthFetch,
    recentRowsLimit = 10,
  ): Promise<ForwardWiringQueueStatusResponse> => {
    const qs = buildQuery({ recent_rows_limit: recentRowsLimit });
    const res = await authFetch(`${BASE}/forward-wiring/queue-status${qs}`);
    return jsonOrThrow<ForwardWiringQueueStatusResponse>(res);
  },

  // ==========================================================================
  // WQ-283 (DEF-023): Operator-Action endpoints
  // ==========================================================================

  acknowledgeEvent: async (
    authFetch: AuthFetch,
    canonicalEventId: string,
    body: AcknowledgeEventBody = {},
  ): Promise<StateTransitionResponse> => {
    const res = await authFetch(
      `${BASE}/events/${canonicalEventId}/acknowledge`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    return jsonOrThrow<StateTransitionResponse>(res);
  },

  snoozeEvent: async (
    authFetch: AuthFetch,
    canonicalEventId: string,
    body: SnoozeEventBody,
  ): Promise<StateTransitionResponse> => {
    const res = await authFetch(
      `${BASE}/events/${canonicalEventId}/snooze`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    return jsonOrThrow<StateTransitionResponse>(res);
  },

  dismissEventFP: async (
    authFetch: AuthFetch,
    canonicalEventId: string,
    body: DismissFPBody,
  ): Promise<StateTransitionResponse> => {
    const res = await authFetch(
      `${BASE}/events/${canonicalEventId}/dismiss-fp`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    return jsonOrThrow<StateTransitionResponse>(res);
  },

  createIntelSource: async (
    authFetch: AuthFetch,
    body: CreateIntelSourceBody,
  ): Promise<IntelSource> => {
    const res = await authFetch(`${BASE}/sources`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return jsonOrThrow<IntelSource>(res);
  },

  // WQ-285 (DEF-024 sibling): list registered threat-intel parsers.
  // AddSourceModal calls this on mount to populate the parser dropdown;
  // falls back to a hardcoded list if the endpoint errors.
  listParsers: async (authFetch: AuthFetch): Promise<ListParsersResponse> => {
    const res = await authFetch(`${BASE}/parsers`);
    return jsonOrThrow<ListParsersResponse>(res);
  },

  // WQ-286 Item 1: re-open from dismissed_fp (terminal-state unfork).
  reopenEvent: async (
    authFetch: AuthFetch,
    canonicalEventId: string,
    body: ReopenEventBody,
  ): Promise<StateTransitionResponse> => {
    const res = await authFetch(
      `${BASE}/events/${canonicalEventId}/reopen`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    return jsonOrThrow<StateTransitionResponse>(res);
  },

  // WQ-286 Item 3: reactivate (un-soft-delete) an intel source.
  reactivateIntelSource: async (
    authFetch: AuthFetch,
    sourceId: string,
  ): Promise<ReactivateSourceResponse> => {
    const res = await authFetch(`${BASE}/sources/${sourceId}/reactivate`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
    return jsonOrThrow<ReactivateSourceResponse>(res);
  },

  deleteIntelSource: async (
    authFetch: AuthFetch,
    sourceId: string,
    hardDelete: boolean,
    body: DeleteIntelSourceBody = {},
  ): Promise<DeleteIntelSourceResponse> => {
    const qs = buildQuery({ hard_delete: hardDelete });
    const res = await authFetch(`${BASE}/sources/${sourceId}${qs}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return jsonOrThrow<DeleteIntelSourceResponse>(res);
  },
};
